import React, { useReducer, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Search } from 'semantic-ui-react';
import { searchApi } from '../../api/searchApi';
import { RootState } from '../../state/reducers';
import styles from "./ComboMaHSFilter.module.css";

interface State {
    loading: boolean;
    results: any[];
    value: string;
}

const initialState: State = {
    loading: false,
    results: [],
    value: '',
};

type Action =
    | { type: 'CLEAN_QUERY' }
    | { type: 'START_SEARCH'; query: string }
    | { type: 'FINISH_SEARCH'; results: any[] }
    | { type: 'UPDATE_SELECTION'; selection: string };

const searchReducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'CLEAN_QUERY':
            return initialState;
        case 'START_SEARCH':
            return { ...state, loading: true, value: action.query };
        case 'FINISH_SEARCH':
            return { ...state, loading: false, results: action.results };
        case 'UPDATE_SELECTION':
            return { ...state, value: action.selection };
        default:
            throw new Error('Unhandled action type');
    }
};

interface ComboMaHSFilterProps {
    onResultChanged?: (result: any) => void;
    defaultValue: { ma_hs: string; ts_hocsinh_hoten?: string };
}

const ComboMaHSFilter: React.FC<ComboMaHSFilterProps> = ({ onResultChanged, defaultValue }) => {
    const [state, dispatch] = useReducer(searchReducer, initialState);
    const { loading, results, value } = state;
    const { nam_hoc, dm_coso_id } = useSelector((x: RootState) => x.common);
    const timeoutRef = useRef<any>();

    const handleSearchChange = useCallback(
        (e:any, data:any) => {
            clearTimeout(timeoutRef.current);
            dispatch({ type: 'START_SEARCH', query: data.value });
            timeoutRef.current = setTimeout(async () => {
                if (data.value.length === 0) {
                    dispatch({ type: 'CLEAN_QUERY' });
                    return;
                }
                const res = await searchApi.Search({
                    key: data.value,
                    dm_coso_id: dm_coso_id, 
                    nam_hoc
                });
                if (res.is_success) {
                    const result = res.data.map((ts_hocsinh: any) => ({
                        title: `${ts_hocsinh.ho_ten} - ${ts_hocsinh.ma_hs}`,
                        ts_hocsinh
                    })) || [];
                    dispatch({ type: 'FINISH_SEARCH', results: result });
                }
            }, 500);
        }, [nam_hoc]);

    useEffect(() => {
        return () => {
            clearTimeout(timeoutRef.current);
        };
    }, []);

    useEffect(() => {
        if (defaultValue.ma_hs) {
            dispatch({ type: 'UPDATE_SELECTION', selection: `${defaultValue.ts_hocsinh_hoten} - ${defaultValue.ma_hs}` });
        } else {
            dispatch({ type: 'CLEAN_QUERY' });
        }
    }, [defaultValue]);

    const resultRenderer = (data: any) => {
        if (data.ts_hocsinh) {
            return (
                <div className={styles.item_container}>
                    <div className={styles.info_container}>
                        <div className={styles.item_title}>{data.ts_hocsinh.ho_ten}</div>
                        <div className={styles.item_description}>{data.ts_hocsinh.ma_hs}</div>
                    </div>
                    <div>
                        <span className={styles.item_tag_hocsinh}>Học sinh</span>
                    </div>
                </div>
            );
        }
        return <></>;
    };

    return (
        <Search
            loading={loading}
            onResultSelect={(e, data) => {
                dispatch({ type: 'UPDATE_SELECTION', selection: data.result.title });
                if (onResultChanged) {
                    onResultChanged(data.result);
                }
            }}
            onSearchChange={handleSearchChange}
            results={results}
            resultRenderer={resultRenderer}
            value={value}
            input={{
                icon: 'search',
                placeholder: 'Tìm kiếm học sinh',
            }}
            fluid
            className={styles.search}
        />
    );
};

export default ComboMaHSFilter;
